import type { SubscriptionStatus } from "./organization";

// #377/#382 admin unconfirmed-charge worklist. Enum values are numeric to match
// the API's enums-as-int serialization (HeldChargeResolutionType on the server).
export enum HeldChargeResolution {
    None = 0,
    Voided = 1,
    Released = 2,
    // #439: an unconfirmed charge (no held record) found settled at the vendor.
    Settled = 3,
}

// The clear endpoints' body. The API refuses a transactionId with any
// resolution but Settled, and requires one with Settled — so only that arm
// carries it. transactionId is the Authorize.Net transaction id: digits only.
export type ClearChargeRequest =
    | {
        resolution:
            | HeldChargeResolution.Voided
            | HeldChargeResolution.Released;
    }
    | { resolution: HeldChargeResolution.Settled; transactionId: string };

// The cleared marker is null on the API's idempotent no-op: the charge was no
// longer blocked, so nothing was changed. The paid period is set only when a
// payment was recorded (Released or Settled).
export interface ClearRenewalChargeResponse {
    clearedRenewalChargeUnconfirmedAt: string | null;
    renewalAttemptCount: number;
    lastChargeFailureAt: string | null;
    resolution: HeldChargeResolution;
    paidPeriodStart: string | null;
    paidPeriodEnd: string | null;
}

export interface ClearActivationChargeResponse {
    clearedActivationChargeUnconfirmedAt: string | null;
    resolution: HeldChargeResolution;
}

// One blocked location activation (Location.ActivationChargeUnconfirmedAt set).
// heldTransactionId is the vendor transaction sitting in Authorize.Net's review
// queue when the block came from a HELD charge; null means a plain unconfirmed
// charge with nothing to release. It is the operator's reconciliation key.
export interface UnconfirmedActivationCharge {
    organizationId: string;
    organizationName: string;
    locationId: string;
    locationName: string;
    unconfirmedSince: string;
    heldTransactionId: string | null;
}

// One blocked membership renewal (Subscription.RenewalChargeUnconfirmedAt set).
// renewalAttemptCount / lastChargeFailureAt are echoed because clearing does NOT
// reset the retry ladder — a spent ladder additionally needs a reactivate.
export interface UnconfirmedRenewalCharge {
    organizationId: string;
    organizationName: string;
    subscriptionId: string;
    planName: string;
    status: SubscriptionStatus;
    unconfirmedSince: string;
    renewalAttemptCount: number;
    lastChargeFailureAt: string | null;
    heldTransactionId: string | null;
}

// One order whose returned-payment fee charge is unconfirmed (#469: the order's
// ReturnedPaymentFeeUnconfirmedAt is set). Any order status; the order may also
// already record a fee (a lost race: refund this charge, then clear it as
// voided). attemptedAmount is always set in practice; the column is nullable.
export interface UnconfirmedReturnedPaymentFee {
    organizationId: string;
    organizationName: string;
    locationId: string;
    locationName: string;
    orderId: string;
    orderNumber: number;
    orderNumberFormatted: string;
    attemptedAmount: number | null;
    unconfirmedSince: string;
    attemptedByUserId: string | null;
    attemptedByUserName: string | null;
    heldTransactionId: string | null;
}

export interface UnconfirmedCharges {
    locations: UnconfirmedActivationCharge[];
    subscriptions: UnconfirmedRenewalCharge[];
    // Oldest first.
    returnedPaymentFees: UnconfirmedReturnedPaymentFee[];
}

// POST /orders/{orderId}/returned-payment-fee/clear-unconfirmed-charge (#469).
// The cleared marker is null on the API's idempotent no-op. The fee fields are
// set only when a fee was recorded (Released or Settled).
export interface ClearReturnedPaymentFeeChargeResponse {
    clearedReturnedPaymentFeeUnconfirmedAt: string | null;
    resolution: HeldChargeResolution;
    returnedPaymentFeeAmount: number | null;
    returnedPaymentFeeTransactionId: string | null;
}
